import { object, string, type InferType } from 'yup';
import type { Maybe, TimeType, TripType } from '@/types';
import type { MaybeRef, Ref } from 'vue';
import { TRIP_BOOKING_TIME_TYPE_INWARD_DEFAULT, TRIP_BOOKING_TIME_TYPE_OUTWARD_DEFAULT } from '@/config';
import { timeFormatValidationRegex } from '@/utils/regex';
import type { FormMode } from '@/components/tripBookingPage/tripBooking.ts';
import { renderRoundTrip } from '@/components/tripBookingPage/helpers/bookingFormHelpers.ts';

export const getTimeSchema = (tripType: Ref<TripType>, formMode: MaybeRef<FormMode>) =>
  object({
    requestedTime: object({
      outward: object({
        type: string().required('generic.pleaseSelectAnOption')
          .oneOf<TimeType>(['arrival', 'departure'])
          .default(TRIP_BOOKING_TIME_TYPE_OUTWARD_DEFAULT)
          .nonNullable(),
        time: string()
          .when('type', {
            is: (val: Maybe<TimeType>) => !!val,
            then: (schema) =>
              schema
                .required('pages.tripBooking.forms.items.timePicker.validations.required.default')
                .matches(timeFormatValidationRegex, { excludeEmptyString: true, message: 'pages.tripBooking.forms.items.timePicker.validations.invalid' }),
          }),
      }).required(),
      inward: object({
        type: string()
          [renderRoundTrip(tripType.value, formMode) ? 'required' : 'notRequired']('generic.pleaseSelectAnOption')
          .oneOf<TimeType>(['arrival', 'departure'])
          .default(TRIP_BOOKING_TIME_TYPE_INWARD_DEFAULT)
          .nonNullable(),
        time: string()
          .when('type', {
            is: (val: Maybe<TimeType>) => renderRoundTrip(tripType.value, formMode) && !!val,
            then: (schema) =>
              schema
                .required('pages.tripBooking.forms.items.timePicker.validations.required.default')
                .matches(timeFormatValidationRegex, { excludeEmptyString: true, message: 'pages.tripBooking.forms.items.timePicker.validations.invalid' }),
          }),
      }),
    }),
  });

export type TimeSchema = InferType<ReturnType<typeof getTimeSchema>>;

/** Sélection de l'heure et du type d'heure demandé dans le formulaire */
export type ITimeSelection = {
  /** Type d'heure entrée:  Heure de départ ou d'arrivée */
  type: TimeType;
  /** Heure du déplacement */
  time: string;
};

/**
 * infos relatives à l'heure du déplacement et la validation backend associée afin de permettre l'affichage
 * d'informations supplémentaires dans le panneau de revue à la dernière étape du formulaire.
 */
export type ITimeValidated = ITimeSelection & (
  | {
    status: 'sameDay';
    date: null;
  }
  | {
    status: 'nextDay';
    date: string;
  }
);

export type ExposedTimePickerProps = {
  focus: () => void;
};
